import { useState } from 'react';
import { FormattingButton } from '../../../components/FormattingButton';
import { supportsAuthoredItalic } from '../../../typography/fonts';

import type { VectorSourceDescriptor } from '../../../data/model';
import { labelFields } from '../../../labels/fields';
import { LABEL_FONT_FAMILIES, type LabelFontFamilyId } from '../../../labels/fonts';
import type { PointLabelPlacement } from '../../../labels/model';
import { LABEL_LIMITS } from '../../../labels/validation';
import type { LayerDefinition } from '../../../project/model';
import { geometryFamiliesForSource } from '../../../styles/geometry';
import type { LabelChangeResult } from '../../state/appStore';
import { useAppStore } from '../../state/useAppStore';
import { ColourControl } from '../style/ColourControl';
import { NumericControl } from '../style/NumericControl';
import { LabelFilterEditor } from './LabelFilterEditor';
import { fieldDisplayLabel } from '../../../joins/effectiveLayer';

interface LabelsInspectorProps {
  readonly layer: LayerDefinition;
  readonly source: VectorSourceDescriptor;
}

const POINT_PLACEMENT_OPTIONS: readonly {
  readonly value: PointLabelPlacement;
  readonly label: string;
}[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'above', label: 'Above' },
  { value: 'below', label: 'Below' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'centre', label: 'Centre' },
];

export function LabelsInspector({ layer, source }: LabelsInspectorProps) {
  const [notice, setNotice] = useState<string | null>(null);
  const setLabelsEnabled = useAppStore((state) => state.setLayerLabelsEnabled);
  const updateLabels = useAppStore((state) => state.updateLayerLabels);
  const suitableFields = labelFields(source.fields);
  const geometryFamilies = geometryFamiliesForSource(source);
  const onlyGeometry = geometryFamilies.length === 1 ? geometryFamilies[0] : null;

  const handleResult = (result: LabelChangeResult) => {
    setNotice(result.ok ? null : result.message);
  };

  return (
    <div
      className="inspector-content style-inspector labels-inspector"
      data-control-density="compact"
    >
      <section className="inspector-section labels-enable-section">
        <div className="labels-enable-row">
          <h3>Labels</h3>
          <button
            aria-checked={layer.labels.enabled}
            aria-label="Labels"
            className="labels-toggle"
            onClick={() => handleResult(setLabelsEnabled(layer.id, !layer.labels.enabled))}
            role="switch"
            type="button"
          >
            {layer.labels.enabled ? 'On' : 'Off'}
          </button>
        </div>
        {!layer.labels.enabled && suitableFields.length === 0 ? (
          <p className="style-help">No scalar attribute fields are available for labels.</p>
        ) : null}
        {notice ? (
          <p className="style-notice" role="status">
            {notice}
          </p>
        ) : null}
      </section>

      <section className="inspector-section">
        <h3>Attribute</h3>
        <div className="style-field">
          <label htmlFor={`label-field-${layer.id}`}>Label field</label>
          <select
            id={`label-field-${layer.id}`}
            onChange={(event) =>
              handleResult(updateLabels(layer.id, { field: event.target.value || null }))
            }
            value={layer.labels.field ?? ''}
          >
            <option value="" disabled={layer.labels.enabled}>
              Choose field
            </option>
            {suitableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {fieldDisplayLabel(field)}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="inspector-section label-filter-section">
        <LabelFilterEditor
          fields={source.fields}
          filter={layer.labels.filter}
          layerId={layer.id}
          onResult={handleResult}
        />
      </section>

      <section className="inspector-section labels-control-group">
        <h3>Text</h3>
        <div className="labels-font-row">
          <div className="style-field">
            <label htmlFor={`label-font-family-${layer.id}`}>Font</label>
            <select
              id={`label-font-family-${layer.id}`}
              onChange={(event) =>
                handleResult(
                  updateLabels(layer.id, {
                    fontFamily: event.target.value as LabelFontFamilyId,
                    ...(!supportsAuthoredItalic(event.target.value as LabelFontFamilyId)
                      ? { fontStyle: 'normal' as const }
                      : {}),
                  }),
                )
              }
              value={layer.labels.fontFamily}
            >
              {LABEL_FONT_FAMILIES.map((family) => (
                <option key={family.id} value={family.id}>
                  {family.label}
                </option>
              ))}
            </select>
          </div>
          <div aria-label="Font style" className="labels-font-style" role="group">
            <FormattingButton
              label="Bold"
              pressed={layer.labels.fontWeight === 'bold'}
              onClick={() =>
                handleResult(
                  updateLabels(layer.id, {
                    fontWeight: layer.labels.fontWeight === 'bold' ? 'regular' : 'bold',
                  }),
                )
              }
            />
            <FormattingButton
              label="Italic"
              pressed={layer.labels.fontStyle === 'italic'}
              disabled={!supportsAuthoredItalic(layer.labels.fontFamily)}
              onClick={() =>
                handleResult(
                  updateLabels(layer.id, {
                    fontStyle: layer.labels.fontStyle === 'italic' ? 'normal' : 'italic',
                  }),
                )
              }
            />
          </div>
        </div>
        <ColourControl
          id={`label-text-colour-${layer.id}`}
          label="Text colour"
          onChange={(textColour) => handleResult(updateLabels(layer.id, { textColour }))}
          value={layer.labels.textColour}
        />
        <NumericControl
          id={`label-text-size-${layer.id}`}
          label="Text size"
          max={LABEL_LIMITS.textSize.maximum}
          min={LABEL_LIMITS.textSize.minimum}
          onChange={(textSize) => handleResult(updateLabels(layer.id, { textSize }))}
          step={1}
          suffix="px"
          value={layer.labels.textSize}
        />
      </section>

      <section className="inspector-section labels-control-group">
        <h3>Placement</h3>
        {onlyGeometry === 'point' ? (
          <>
            <div className="style-field">
              <label htmlFor={`label-placement-${layer.id}`}>Placement</label>
              <select
                id={`label-placement-${layer.id}`}
                onChange={(event) =>
                  handleResult(
                    updateLabels(layer.id, {
                      pointPlacement: event.target.value as PointLabelPlacement,
                    }),
                  )
                }
                value={layer.labels.pointPlacement}
              >
                {POINT_PLACEMENT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <NumericControl
              id={`label-point-offset-${layer.id}`}
              label="Offset"
              max={LABEL_LIMITS.pointOffset.maximum}
              min={LABEL_LIMITS.pointOffset.minimum}
              onChange={(pointOffset) => handleResult(updateLabels(layer.id, { pointOffset }))}
              step={1}
              suffix="px"
              value={layer.labels.pointOffset}
            />
          </>
        ) : onlyGeometry === 'line' ? (
          <>
            <p className="labels-placement-note">Text follows the line automatically.</p>
            <NumericControl
              id={`label-line-offset-${layer.id}`}
              label="Line offset"
              max={LABEL_LIMITS.lineOffset.maximum}
              min={LABEL_LIMITS.lineOffset.minimum}
              onChange={(lineOffset) => handleResult(updateLabels(layer.id, { lineOffset }))}
              step={1}
              suffix="px"
              value={layer.labels.lineOffset}
            />
          </>
        ) : onlyGeometry === 'polygon' ? (
          <p className="labels-placement-note">
            One label is placed automatically inside each feature.
          </p>
        ) : (
          <p className="labels-placement-note">
            Placement adapts automatically to each geometry type.
          </p>
        )}
        <NumericControl
          id={`label-collision-spacing-${layer.id}`}
          label="Collision spacing"
          max={LABEL_LIMITS.collisionSpacing.maximum}
          min={LABEL_LIMITS.collisionSpacing.minimum}
          onChange={(collisionSpacing) =>
            handleResult(updateLabels(layer.id, { collisionSpacing }))
          }
          step={1}
          suffix="px"
          value={layer.labels.collisionSpacing}
        />
      </section>

      <section className="inspector-section labels-control-group">
        <h3>Halo</h3>
        <ColourControl
          id={`label-halo-colour-${layer.id}`}
          label="Halo colour"
          onChange={(haloColour) => handleResult(updateLabels(layer.id, { haloColour }))}
          value={layer.labels.haloColour}
        />
        <NumericControl
          id={`label-halo-width-${layer.id}`}
          label="Halo width"
          max={LABEL_LIMITS.haloWidth.maximum}
          min={LABEL_LIMITS.haloWidth.minimum}
          onChange={(haloWidth) => handleResult(updateLabels(layer.id, { haloWidth }))}
          step={0.5}
          suffix="px"
          value={layer.labels.haloWidth}
        />
      </section>
    </div>
  );
}
